import {formatThousands, isNumericEntry, stripThousands} from '../utils/format-number';
import {publishPaymentUpdate, subscribePaymentUpdate} from '../utils/payment-plan-update-sync';
import {draftFieldPatch, paymentUpdateValidationErrors} from '../utils/payment-update-fields';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {useCallback, useEffect, useRef, useState, type ChangeEvent, type ReactNode} from 'react';
import type {StepDetail} from '../dtos';
import {paymentPlanUpdatesApi} from '../api/payment-plan-updates';
import {getStepDetail} from '../api';
import type {PaymentUpdateDocument, PaymentUpdateFile, PaymentUpdateImpact, PaymentUpdateRevision, PaymentUpdateStep, PaymentUpdateTerms, PaymentUpdateView} from '../dtos/payment-plan-update';
import {editablePaymentTerms, paymentUpdateLabel, isStalePaymentUpdate, paymentPlanUpdateError, paymentTermsError, samePaymentTerms} from '../utils/payment-plan-update';
import {detach} from '../utils/detach';
import {PaymentPlanUpdateForm} from './payment-plan-update-form';

const TERM_LABELS: [keyof PaymentUpdateTerms, string][] = [
	['cashPrice', 'Cash price'], ['downPayment', 'Down payment'], ['loanAmount', 'Loan amount'],
	['tenorYears', 'Tenor (years)'], ['frequency', 'Frequency'], ['leadingMonths', 'Special installment count'],
	['financeProduct', 'Finance product'], ['propertyUsage', 'Property usage'],
];
/** `??` would keep an authored empty string; blank text has to fall through the way `||` did. */
const orFallback = (value: string | null | undefined, fallback: string): string => value?.length ? value : fallback;
function formatTerm(value: unknown): string {
	if (value == null) return 'Not yet approved';
	if (typeof value === 'number') return value.toLocaleString('en-EG', {maximumFractionDigits: 2});
	return String(value);
}

interface ComparisonProps {previous: PaymentUpdateTerms; proposed: PaymentUpdateTerms; approved: PaymentUpdateTerms | null; proposedLabel?: string}
export function PaymentUpdateComparison({previous, proposed, approved, proposedLabel = 'Proposed'}: Readonly<ComparisonProps>) {
	return <Box sx={{overflowX: 'auto'}}><Table size="small" aria-label="Payment plan comparison">
		<TableHead><TableRow><TableCell>Payment terms</TableCell><TableCell>Previously submitted</TableCell><TableCell>{proposedLabel}</TableCell><TableCell>Last Credit-approved</TableCell></TableRow></TableHead>
		<TableBody>{TERM_LABELS.map(([key, label]) => <TableRow key={key} selected={previous[key] !== proposed[key]}>
			<TableCell component="th" scope="row">{label}</TableCell><TableCell>{formatTerm(previous[key])}</TableCell><TableCell>{formatTerm(proposed[key])}</TableCell><TableCell>{formatTerm(approved?.[key])}</TableCell>
		</TableRow>)}</TableBody>
	</Table></Box>;
}

/** An authored label wins, unless the server only echoed the document key back at us. */
function documentTitle(document: PaymentUpdateDocument): string {
	const authored = document.label;
	if (authored?.trim() && authored !== document.documentKey && authored !== document.key) return authored;
	return paymentUpdateLabel(document.documentKey || document.key);
}
function documentStatus(document: PaymentUpdateDocument): string {
	if (document.satisfied) return 'Received';
	if (document.requiresNewSignature) return 'Upload a newly signed copy after the revised document is generated.';
	return 'A new document is required.';
}
function FreshDocuments({documents}: Readonly<{documents: PaymentUpdateDocument[]}>) {
	return <Stack spacing={0.5}>{documents.map(document => <Typography key={document.key} variant="body2">
		{documentTitle(document)} — {documentStatus(document)}
	</Typography>)}</Stack>;
}

const STEP_GROUPS: {disposition: NonNullable<PaymentUpdateStep['disposition']>; title: string; hint: string}[] = [
	{disposition: 'continues', title: 'Already being reviewed', hint: 'These reviews are open now. The reviewer sees the new terms; nothing restarts.'},
	{disposition: 'reopens', title: 'Will be reviewed again', hint: 'These were already approved and must be approved again with the new terms.'},
	{disposition: 'upcoming', title: 'Coming up later', hint: 'These have not started yet; they will use the new terms when their turn comes.'},
];
const sentence = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);
function stepReasonText(step: PaymentUpdateStep): string {
	return (step.reasons ?? []).map(reason => orFallback(reason.summary, reason.name)).join('; ');
}
function RepeatStep({step}: Readonly<{step: PaymentUpdateStep}>) {
	const reasons = stepReasonText(step);
	return <Box sx={{mb: 0.5}}>
		<Typography variant="body2">{step.name}</Typography>
		{reasons && <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>Why: {reasons}</Typography>}
	</Box>;
}
function RepeatSteps({steps}: Readonly<{steps: PaymentUpdateStep[]}>) {
	if (!steps.length) return <Typography variant="body2">No additional reviews.</Typography>;
	if (!steps.some(step => step.disposition)) return <>{steps.map(step => <RepeatStep step={step} key={step.uid} />)}</>;
	return <Stack spacing={1.5}>{STEP_GROUPS.map(group => {
		const members = steps.filter(step => step.disposition === group.disposition);
		return members.length ? <Box key={group.disposition}>
			<Typography variant="body2" sx={{fontWeight: 600}}>{group.title}</Typography>
			<Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.5}}>{group.hint}</Typography>
			{members.map(step => <RepeatStep step={step} key={step.uid} />)}
		</Box> : null;
	})}</Stack>;
}
function MatchingRule({rule}: Readonly<{rule: PaymentUpdateImpact['matchingRules'][number]}>) {
	return <Box sx={{mb: 0.5}}>
		<Typography variant="body2">{sentence(orFallback(rule.summary, rule.name))}</Typography>
		{(rule.details ?? []).map(detail => <Typography variant="caption" color="text.secondary" sx={{display: 'block'}} key={detail}>{detail}</Typography>)}
	</Box>;
}
function Chevron() {
	return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor" /></svg>;
}
/** Every impact section starts collapsed so the summary stays short; the count says what is inside. */
function ImpactSection({title, count, children}: Readonly<{title: string; count: number; children: ReactNode}>) {
	return <Accordion disableGutters variant="outlined" defaultExpanded={false} slotProps={{transition: {unmountOnExit: true}}}>
		<AccordionSummary expandIcon={<Chevron />}><Typography sx={{fontWeight: 600}}>{title} ({count})</Typography></AccordionSummary>
		<AccordionDetails>{children}</AccordionDetails>
	</Accordion>;
}
function NotificationLine({notification}: Readonly<{notification: PaymentUpdateImpact['notifications'][number]}>) {
	return <Typography variant="body2">{orFallback(notification.templateName, paymentUpdateLabel(notification.templateKey))} — {orFallback(notification.teamName, 'Assigned team')}.</Typography>;
}

function ImpactPreview({impact}: Readonly<{impact: PaymentUpdateImpact}>) {
	if (!impact.canSubmit && impact.changedFields.length === 0) return <Stack spacing={2}><PaymentUpdateComparison previous={impact.previous} proposed={impact.proposed} approved={impact.approvedBaseline} />{impact.blockers.map(blocker => <Alert severity="info" key={blocker}>{blocker}</Alert>)}</Stack>;
	return <Stack spacing={2}>
		<Typography variant="h6">What happens after submission</Typography>
		<PaymentUpdateComparison previous={impact.previous} proposed={impact.proposed} approved={impact.approvedBaseline} />
		<Alert severity={impact.dbr.withinDbr ? 'info' : 'warning'}>
			On the client's current income they qualify for a loan up to {formatTerm(impact.dbr.maxLoan)} over {impact.dbr.tenorYears} years (installment up to {formatTerm(impact.dbr.availableInstallment)}).
			{!impact.dbr.withinDbr && ' The new loan is above this, so Credit must reassess it.'}
		</Alert>
		<Box>
			{impact.matchingRules.length > 0 && <ImpactSection title="Why these reviews are needed" count={impact.matchingRules.length}>{impact.matchingRules.map(rule => <MatchingRule rule={rule} key={rule.key} />)}</ImpactSection>}
			<ImpactSection title="Reviews to repeat" count={impact.repeatSteps.length}><RepeatSteps steps={impact.repeatSteps} /></ImpactSection>
			{impact.documents.length > 0 && <ImpactSection title="Fresh documents" count={impact.documents.length}><FreshDocuments documents={impact.documents} /></ImpactSection>}
			{impact.notifications.length > 0 && <ImpactSection title="Messages after submission" count={impact.notifications.length}>{impact.notifications.map(notification => <NotificationLine key={`${notification.templateKey}·${notification.teamUid}`} notification={notification} />)}</ImpactSection>}
		</Box>
		{impact.requiresIncomeDocuments && <Alert severity="warning">New income documents are required for this assessment. Upload them below, then review the impact again.</Alert>}
		{impact.blockers.map(blocker => <Alert severity="warning" key={blocker}>{blocker}</Alert>)}
	</Stack>;
}

function downloadDraft(file: PaymentUpdateFile): void {
	const bytes = Uint8Array.from(atob(file.base64), character => character.charCodeAt(0));
	const url = URL.createObjectURL(new Blob([bytes], {type: file.mimeType}));
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = file.fileName;
	anchor.click();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const sameFormData = (left: Record<string, unknown>, right: Record<string, unknown>): boolean =>
	JSON.stringify(Object.entries(left).sort(([a], [b]) => a.localeCompare(b))) === JSON.stringify(Object.entries(right).sort(([a], [b]) => a.localeCompare(b)));

const REVISION_STATUS_LABEL: Record<PaymentUpdateRevision['status'], string> = {
	pending: 'Awaiting reviews', rejected: 'Changes requested', completed: 'Approved',
};
function ObligationStep({step}: Readonly<{step: PaymentUpdateStep}>) {
	const reasons = stepReasonText(step);
	return <Typography variant="body2">{step.name} — {step.status.replace(/_/g, ' ').toLowerCase()}{reasons && <Typography component="span" variant="body2" color="text.secondary"> · Why: {reasons}</Typography>}</Typography>;
}
/** What a submitted revision is doing right now — its generation state, feedback and obligations. */
function RevisionContext({view, busy, onRetryGeneration}: Readonly<{view: PaymentUpdateView; busy: boolean; onRetryGeneration: () => void}>) {
	const revision = view.revision;
	if (!revision) return null;
	const generation = revision.generation;
	const retry = view.canEdit && generation?.canRetry ? <Button disabled={busy} onClick={onRetryGeneration}>Retry document generation</Button> : undefined;
	return <>
		{generation && generation.status !== 'ready' && <Alert severity={generation.status === 'failed' ? 'warning' : 'info'} action={retry}>
			{generation.status === 'failed' ? 'The update was submitted, but its official documents could not be generated. Retry document generation to continue.' : 'The update was submitted. Its official documents are being generated.'}
		</Alert>}
		{revision.status === 'rejected' && <Alert severity="warning">Payment-plan update returned to Sales: {orFallback(revision.rejectionReason, 'Open the review feedback for details.')}</Alert>}
		<Box component="details"><Typography component="summary" sx={{cursor: 'pointer', fontWeight: 600}}>Submitted payment plan · {REVISION_STATUS_LABEL[revision.status]}</Typography>
			<PaymentUpdateComparison previous={revision.previous} proposed={revision.proposed} approved={revision.approvedBaseline} proposedLabel="Submitted update" />
			{view.obligations.steps.map(step => <ObligationStep key={step.uid} step={step} />)}
			<FreshDocuments documents={view.obligations.documents} />
		</Box>
	</>;
}

interface EditorNoticesProps {
	error: string;
	stale: boolean;
	busy: boolean;
	validationError: string;
	notice: string;
	/** the dialog dims its confirmation line; the inline editor keeps it at full contrast */
	dimNotice?: boolean;
	onReloadLatest: () => void;
	onKeepEdits: () => void;
}
function EditorNotices({error, stale, busy, validationError, notice, dimNotice, onReloadLatest, onKeepEdits}: Readonly<EditorNoticesProps>) {
	const reload = stale ? <Button disabled={busy} onClick={onReloadLatest}>Reload latest plan</Button> : undefined;
	return <>
		{error && <Alert severity="error" action={reload}>{error}</Alert>}
		{stale && <Typography variant="body2">Reloading replaces your local edits. Keep them only after reviewing the latest saved plan.</Typography>}
		{stale && <Button disabled={busy} onClick={onKeepEdits}>Keep my edits on latest plan</Button>}
		{validationError && <Alert severity="error">{validationError}</Alert>}
		{notice && <Typography component="output" variant="body2" color={dimNotice ? 'text.secondary' : undefined}>{notice}</Typography>}
	</>;
}

interface IncomeDocumentsProps {
	view: PaymentUpdateView;
	disabled: boolean;
	actionsDisabled: boolean;
	/** the dialog lays the list out narrower, so long file names wrap there */
	wrapFileName?: boolean;
	onRemoveAttachment: (uid: string) => void;
	onUpload: (file: File) => void;
}
function IncomeDocuments({view, disabled, actionsDisabled, wrapFileName, onRemoveAttachment, onUpload}: Readonly<IncomeDocumentsProps>) {
	const pick = (event: ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (file) onUpload(file);
	};
	return <Box><Typography sx={{fontWeight: 600}}>New income documents</Typography><Typography variant="body2" color="text.secondary">These files are held with this draft until submission.</Typography>
		{view.draft?.attachments.map(attachment => <Stack key={attachment.uid} direction="row" spacing={1} sx={{alignItems: 'center'}}>
			<Typography variant="body2" sx={wrapFileName ? {overflowWrap: 'anywhere'} : undefined}>{attachment.fileName}</Typography>
			<Button disabled={disabled} onClick={() => onRemoveAttachment(attachment.uid)}>Remove</Button>
		</Stack>)}
		<Button component="label" disabled={actionsDisabled}>Upload income document<input hidden type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={pick} /></Button></Box>;
}

function DraftFileActions({actionsDisabled, onDownloadPdf, onPrepareShare}: Readonly<{actionsDisabled: boolean; onDownloadPdf: () => void; onPrepareShare: () => void}>) {
	return <>
		<Button disabled={actionsDisabled} onClick={onDownloadPdf}>Download draft PDF</Button>
		<Button disabled={actionsDisabled} onClick={onPrepareShare}>Prepare draft to share</Button>
	</>;
}

/** The plain term inputs, used when the step serves no payment-plan form schema of its own. */
function TermsGrid({terms, disabled, canEditPropertyUsage, onChange}: Readonly<{terms: PaymentUpdateTerms; disabled: boolean; canEditPropertyUsage?: boolean; onChange: (patch: Partial<PaymentUpdateTerms>) => void}>) {
	return <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 2}}>
		{(['cashPrice', 'downPayment', 'tenorYears'] as const).map(key => <TextField key={key} label={TERM_LABELS.find(([term]) => term === key)?.[1]} type="text" value={formatThousands(terms[key])} disabled={disabled} slotProps={{htmlInput: {inputMode: 'decimal'}}} onChange={event => {const raw = stripThousands(event.target.value); if (isNumericEntry(raw)) onChange({[key]: Number(raw)});}} />)}
		<TextField select label="Frequency" value={terms.frequency} disabled={disabled} onChange={event => onChange({frequency: event.target.value as PaymentUpdateTerms['frequency']})}>{['Monthly', 'Quarterly', 'Special'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
		{terms.frequency === 'Special' && <TextField label="Special installment count" type="text" value={terms.leadingMonths} disabled={disabled} onChange={event => onChange({leadingMonths: Number(event.target.value)})} />}
		<TextField label="Loan amount" value={terms.downPayment >= terms.cashPrice ? 'Check down payment' : formatTerm(terms.cashPrice - terms.downPayment)} slotProps={{input: {readOnly: true}}} helperText="Cash price less down payment" />
		<TextField label="Finance product" value={terms.financeProduct ?? 'Not set'} slotProps={{input: {readOnly: true}}} />
		{canEditPropertyUsage ? <TextField select label="Property usage" value={terms.propertyUsage === 'Residential' || terms.propertyUsage === 'Commercial' ? terms.propertyUsage : ''} disabled={disabled} onChange={event => onChange({propertyUsage: event.target.value})}>
			<MenuItem value="" disabled>{terms.propertyUsage ?? 'Not set'}</MenuItem>
			{['Residential', 'Commercial'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
		</TextField> : <TextField label="Property usage" value={terms.propertyUsage ?? 'Not set'} slotProps={{input: {readOnly: true}}} />}
	</Box>;
}

/** Everything the two editor surfaces read and every write they can start. */
interface EditorContext {
	view: PaymentUpdateView;
	terms: PaymentUpdateTerms | null;
	formData: Record<string, unknown>;
	editorStep: StepDetail | null;
	impact: PaymentUpdateImpact | null;
	busy: boolean;
	stale: boolean;
	error: string;
	notice: string;
	dirty: boolean;
	disabled: boolean;
	actionsDisabled: boolean;
	validationError: string;
	onTermsChange: (patch: Partial<PaymentUpdateTerms>) => void;
	onFieldChange: (key: string, value: unknown) => void;
	onReloadLatest: () => void;
	onKeepEdits: () => void;
	onRetryGeneration: () => void;
	onDownloadPdf: () => void;
	onPrepareShare: () => void;
	onUpload: (file: File) => void;
	onRemoveAttachment: (uid: string) => void;
	onSaveDraft: () => void;
	onReviewImpact: () => void;
	onDiscard: () => void;
	onSubmit: () => void;
}
const discardDisabled = (editor: EditorContext): boolean => editor.disabled || (!editor.view.draft && !editor.dirty);
const submitDisabled = (editor: EditorContext, impact: PaymentUpdateImpact): boolean =>
	editor.actionsDisabled || !impact.canSubmit || impact.version !== editor.view.version;

function InlineEditor({editor}: Readonly<{editor: EditorContext}>) {
	const {view, terms, formData, impact, busy, disabled, actionsDisabled, validationError} = editor;
	const incomeDocuments = <IncomeDocuments view={view} disabled={disabled} actionsDisabled={actionsDisabled} onRemoveAttachment={editor.onRemoveAttachment} onUpload={editor.onUpload} />;
	return <Box sx={{my: 1.5}}><Stack spacing={2}>
		<RevisionContext view={view} busy={busy} onRetryGeneration={editor.onRetryGeneration} />
		<EditorNotices error={editor.error} stale={editor.stale} busy={busy} validationError={validationError} notice={editor.notice} onReloadLatest={editor.onReloadLatest} onKeepEdits={editor.onKeepEdits} />
		{terms && editor.editorStep && <PaymentPlanUpdateForm step={editor.editorStep} terms={terms} formData={formData} editableFieldKeys={view.editableFieldKeys ?? []} canEditPropertyUsage={view.canEditPropertyUsage} disabled={disabled} validationError={validationError} onTermsChange={editor.onTermsChange} onFieldChange={editor.onFieldChange} incomeDocuments={incomeDocuments} />}
		{impact && <ImpactPreview impact={impact} />}
		<Stack direction="row" spacing={1} useFlexGap sx={{flexWrap: 'wrap', justifyContent: 'flex-end'}}>
			<DraftFileActions actionsDisabled={actionsDisabled} onDownloadPdf={editor.onDownloadPdf} onPrepareShare={editor.onPrepareShare} />
			{busy && <CircularProgress size={18} aria-label="Saving payment plan" />}
			<Button color="error" disabled={discardDisabled(editor)} onClick={editor.onDiscard}>Discard draft</Button>
			<Button disabled={actionsDisabled} onClick={editor.onSaveDraft}>Save draft</Button>
			<Button disabled={actionsDisabled} onClick={editor.onReviewImpact}>Review impact</Button>
			{impact && <Button variant="contained" disabled={submitDisabled(editor, impact)} onClick={editor.onSubmit}>Submit update</Button>}
		</Stack>
	</Stack></Box>;
}

/** The closed-editor header: the entry point, plus whatever the last action left behind. */
function EditorLauncher({editor, open, onOpen}: Readonly<{editor: EditorContext; open: boolean; onOpen: () => void}>) {
	const {view, terms, error, notice, dirty} = editor;
	return <Stack spacing={1}>
		<RevisionContext view={view} busy={editor.busy} onRetryGeneration={editor.onRetryGeneration} />
		{view.canEdit && terms ? <Box><Button variant="outlined" onClick={onOpen}>{view.draft ? 'Continue payment-plan draft' : 'Update payment plan'}</Button></Box>
			: view.unavailableReason && <Typography variant="body2" color="text.secondary">{view.unavailableReason}</Typography>}
		{!open && error && <Alert severity="error">{error}</Alert>}
		{!open && notice && <Alert severity={dirty ? 'info' : 'success'}>{notice}</Alert>}
	</Stack>;
}

function EditorDialog({editor, open, onClose}: Readonly<{editor: EditorContext; open: boolean; onClose: () => void}>) {
	const {view, terms, formData, editorStep, impact, busy, disabled, actionsDisabled, validationError} = editor;
	const incomeDocuments = <IncomeDocuments view={view} disabled={disabled} actionsDisabled={actionsDisabled} wrapFileName={!editorStep} onRemoveAttachment={editor.onRemoveAttachment} onUpload={editor.onUpload} />;
	return <Dialog open={open} onClose={onClose} fullWidth maxWidth="tablet" aria-labelledby="payment-update-title">
		<DialogTitle id="payment-update-title">Update payment plan</DialogTitle>
		<DialogContent dividers><Stack spacing={2}>
			<Typography variant="body2" color="text.secondary">Drafts are saved separately. Reviews and messages start only after you submit the update.</Typography>
			<EditorNotices error={editor.error} stale={editor.stale} busy={busy} validationError={validationError} notice={editor.notice} dimNotice onReloadLatest={editor.onReloadLatest} onKeepEdits={editor.onKeepEdits} />
			{terms && editorStep && <PaymentPlanUpdateForm step={editorStep} terms={terms} formData={formData} editableFieldKeys={view.editableFieldKeys ?? []} canEditPropertyUsage={view.canEditPropertyUsage} disabled={disabled} validationError={validationError} onTermsChange={editor.onTermsChange} onFieldChange={editor.onFieldChange} incomeDocuments={incomeDocuments} />}
			{terms && !editorStep && <TermsGrid terms={terms} disabled={disabled} canEditPropertyUsage={view.canEditPropertyUsage} onChange={editor.onTermsChange} />}
			{impact && <ImpactPreview impact={impact} />}
			{!editorStep && incomeDocuments}
			<Stack direction="row" spacing={1} useFlexGap sx={{flexWrap: 'wrap'}}>
				<DraftFileActions actionsDisabled={actionsDisabled} onDownloadPdf={editor.onDownloadPdf} onPrepareShare={editor.onPrepareShare} />
				<Button color="error" disabled={discardDisabled(editor)} onClick={editor.onDiscard}>Discard draft</Button>
			</Stack>
		</Stack></DialogContent>
		<DialogActions sx={{flexWrap: 'wrap', gap: 1}}>
			{busy && <CircularProgress size={18} aria-label="Saving payment plan" />}
			<Button disabled={busy} onClick={onClose}>Close</Button>
			<Button disabled={actionsDisabled} onClick={editor.onSaveDraft}>Save draft</Button>
			<Button disabled={actionsDisabled} onClick={editor.onReviewImpact}>Review impact</Button>
			{impact && <Button variant="contained" disabled={submitDisabled(editor, impact)} onClick={editor.onSubmit}>Submit update</Button>}
		</DialogActions>
	</Dialog>;
}

const liveValidationErrors = (terms: PaymentUpdateTerms | null, editorStep: StepDetail | null, formData: Record<string, unknown>): string[] => {
	if (!terms) return [];
	const stepErrors = editorStep ? paymentUpdateValidationErrors(editorStep, terms, formData) : [];
	return [paymentTermsError(terms), ...stepErrors].filter((message): message is string => Boolean(message));
};
const hasLocalEdits = (terms: PaymentUpdateTerms | null, view: PaymentUpdateView | null, editorStep: StepDetail | null, formData: Record<string, unknown>): boolean => {
	if (!terms || !view) return false;
	const termsChanged = !samePaymentTerms(terms, view.draft?.terms ?? view.current ?? terms);
	return termsChanged || Boolean(editorStep && !sameFormData(formData, view.draft?.formData ?? view.formData ?? {}));
};

/** Case-level editing deliberately never calls the ordinary step submission or completion APIs. */
export function PaymentPlanUpdate({caseUid, refreshKey = 0, onChanged, inlineStep}: Readonly<{caseUid: string; refreshKey?: unknown; onChanged?: () => void; inlineStep?: StepDetail}>) {
	const [view, setView] = useState<PaymentUpdateView | null>(null);
	const [terms, setTerms] = useState<PaymentUpdateTerms | null>(null);
	const [formData, setFormData] = useState<Record<string, unknown>>({});
	const [dialogStep, setDialogStep] = useState<StepDetail | null>(null);
	const [open, setOpen] = useState(false);
	const [impact, setImpact] = useState<PaymentUpdateImpact | null>(null);
	const [busy, setBusy] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [stale, setStale] = useState(false);
	const [notice, setNotice] = useState('');
	const lock = useRef(false);
	const source = useRef({});
	const highestVersion = useRef(-1);
	const editState = useRef({dirty: false, view: null as PaymentUpdateView | null});
	/** Guarded write: a view older than the one already shown never replaces it. */
	const applyView = (next: PaymentUpdateView): boolean => {
		if (next.version < highestVersion.current) return false;
		highestVersion.current = next.version;
		editState.current.view = next;
		setView(next); publishPaymentUpdate(caseUid, next, source.current);
		return true;
	};
	const openRef = useRef(false);
	const requestGeneration = useRef(0);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const editorStep = inlineStep ?? dialogStep;
	const validationError = liveValidationErrors(terms, editorStep, formData).join(' ');
	const cancelAutosave = useCallback(() => {if (timer.current) clearTimeout(timer.current);}, []);
	const receive = useCallback((next: PaymentUpdateView, mode: 'refresh' | 'replace' | 'keep' = 'refresh'): void => {
		const previous = editState.current.view;
		if (next.version < highestVersion.current) return;
		highestVersion.current = next.version;
		const preserveEdits = mode === 'keep';
		if (editState.current.dirty && previous && next.version !== previous.version && mode === 'refresh') {
			cancelAutosave(); setStale(true); setImpact(null);
			setError('This payment plan changed. Your local edits are preserved. Review the latest plan before choosing which edits to keep.');
			return;
		}
		if (mode === 'replace') editState.current.dirty = false;
		editState.current.view = next;
		setView(next);
		if (!preserveEdits && !editState.current.dirty) {setTerms(next.draft?.terms ?? next.current); setFormData(next.draft?.formData ?? next.formData ?? {});}
		setError(''); setStale(false); setImpact(null);
	}, [cancelAutosave]);
	const reload = useCallback(async (mode: 'refresh' | 'replace' | 'keep' = 'refresh') => {
		const generation = ++requestGeneration.current;
		setLoading(true);
		try {
			const next = await paymentPlanUpdatesApi.get(caseUid);
			if (generation !== requestGeneration.current) return;
			receive(next, mode);
			if (inlineStep) openRef.current = true;
		} catch (caught) {if (generation === requestGeneration.current) setError(paymentPlanUpdateError(caught));}
		finally {if (generation === requestGeneration.current) setLoading(false);}
	}, [caseUid, inlineStep, receive]);
	useEffect(() => {if (!lock.current) detach(reload());}, [reload, refreshKey]);
	useEffect(() => subscribePaymentUpdate(caseUid, (next, origin) => {
		if (origin !== source.current) {requestGeneration.current += 1; receive(next);}
	}), [caseUid, receive]);
	useEffect(() => () => {requestGeneration.current += 1; cancelAutosave();}, [cancelAutosave]);
	const fail = (caught: unknown): void => {
		setError(paymentPlanUpdateError(caught));
		if (isStalePaymentUpdate(caught)) setStale(true);
	};
	const run = async (action: () => Promise<void>): Promise<void> => {
		cancelAutosave();
		if (lock.current) return;
		requestGeneration.current += 1;
		lock.current = true; setBusy(true); setError(''); setNotice('');
		try {await action();} catch (caught) {fail(caught);} finally {requestGeneration.current += 1; lock.current = false; setBusy(false);}
	};
	const save = async (): Promise<PaymentUpdateView> => {
		if (!view || !terms) throw new Error('Payment terms are unavailable. Reload the application.');
		const validation = validationError;
		if (validation) throw new Error(validation);
		if (view.draft && samePaymentTerms(view.draft.terms, terms) && (!editorStep || sameFormData(formData, view.draft.formData ?? {}))) return view;
		const ancillary = editorStep ? draftFieldPatch(editorStep, terms, formData, view.draft?.formData ?? view.formData ?? {}, view.editableFieldKeys ?? [], view.formData ?? {}) : undefined;
		const next = editorStep
			? await paymentPlanUpdatesApi.saveDraft(caseUid, view.version, editablePaymentTerms(terms), ancillary)
			: await paymentPlanUpdatesApi.saveDraft(caseUid, view.version, editablePaymentTerms(terms));
		if (!applyView(next)) throw new Error('A newer payment plan is already displayed. Review it before continuing.');
		setTerms(current => current && samePaymentTerms(current, terms) ? next.draft?.terms ?? next.current : current); setImpact(null); setNotice('Draft saved');
		setFormData(current => sameFormData(current, formData) ? next.draft?.formData ?? next.formData ?? {} : current);
		return next;
	};
	const dirty = hasLocalEdits(terms, view, editorStep, formData);
	useEffect(() => {editState.current = {dirty, view};}, [dirty, view]);
	useEffect(() => {
		if ((!open && !inlineStep) || !dirty || !terms || !view?.canEdit || busy || error || stale || validationError) return;
		// Opening the editor alone is read-only. An untouched current plan never creates a draft.
		if (!view.draft && view.current && samePaymentTerms(terms, view.current) && (!editorStep || sameFormData(formData, view.formData ?? {}))) return;
		timer.current = setTimeout(() => {detach(run(async () => {await save();}));}, 900);
		return cancelAutosave;
		// Captures the exact terms/version this timer belongs to; cancel whenever either changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [terms, formData, view, open, inlineStep, editorStep, dirty, busy, error, stale, validationError, cancelAutosave]);
	const change = (patch: Partial<PaymentUpdateTerms>): void => {
		requestGeneration.current += 1;
		setTerms(current => {
			if (!current) return current;
			const next = {...current, ...patch};
			return {...next, leadingMonths: next.frequency === 'Special' ? next.leadingMonths : 0, loanAmount: next.cashPrice - next.downPayment};
		});
		setImpact(null); setNotice(''); if (!stale) setError('');
	};
	const changeFormField = (key: string, value: unknown): void => {
		if (!view?.editableFieldKeys?.includes(key)) return;
		requestGeneration.current += 1; setFormData(current => ({...current, [key]: value}));
		setImpact(null); setNotice(''); if (!stale) setError('');
	};
	const finishClose = (): void => {cancelAutosave(); openRef.current = false; setOpen(false);};
	const close = (): void => {
		if (busy) return;
		if (dirty && validationError) {
			finishClose();
			setNotice('Unsaved changes are kept in this tab. Reopen the editor to correct them.');
			return;
		}
		if (dirty && view?.canEdit && !stale) detach(run(async () => {await save(); finishClose();}));
		else finishClose();
	};
	if (loading && !view) return <Box sx={{py: 1}}><CircularProgress size={18} aria-label="Loading payment plan" /></Box>;
	if (!view) {
		if (!error) return null;
		return <Alert severity="error" action={<Button onClick={() => detach(reload())}>Retry</Button>}>{error}</Alert>;
	}
	const disabled = busy || stale || !view.canEdit;
	const actionsDisabled = disabled || Boolean(validationError) || (open && view.editableFieldKeys !== undefined && !editorStep);
	const discard = async (closeAfter: boolean): Promise<void> => {
		const next = view.draft ? await paymentPlanUpdatesApi.discardDraft(caseUid, view.version) : view;
		if (!applyView(next)) return;
		setTerms(next.current);
		setFormData(next.formData ?? {});
		setImpact(null);
		setNotice('Draft discarded');
		if (closeAfter) finishClose();
	};
	const submit = async (closeAfter: boolean): Promise<void> => {
		if (!impact) return;
		const next = await paymentPlanUpdatesApi.submit(caseUid, impact.version);
		if (!applyView(next)) return;
		setTerms(next.current);
		if (!closeAfter) setFormData(next.formData ?? {});
		setImpact(null);
		if (closeAfter) {openRef.current = false; setOpen(false);}
		setNotice('Payment-plan update submitted. Required reviews are now being arranged.');
		onChanged?.();
	};
	const editor: EditorContext = {
		view, terms, formData, editorStep, impact, busy, stale, error, notice, dirty, disabled, actionsDisabled, validationError,
		onTermsChange: change,
		onFieldChange: changeFormField,
		onReloadLatest: () => detach(run(() => reload('replace'))),
		onKeepEdits: () => detach(run(() => reload('keep'))),
		onRetryGeneration: () => detach(run(async () => {
			const number = view.revision?.number;
			if (number !== undefined) applyView(await paymentPlanUpdatesApi.retryGeneration(caseUid, number));
		})),
		onDownloadPdf: () => detach(run(async () => {const saved = await save(); downloadDraft(await paymentPlanUpdatesApi.pdf(caseUid, saved.version));})),
		onPrepareShare: () => detach(run(async () => {const saved = await save(); downloadDraft(await paymentPlanUpdatesApi.share(caseUid, saved.version)); setNotice('Draft downloaded and ready to share.');})),
		onUpload: file => detach(run(async () => {const saved = await save(); applyView(await paymentPlanUpdatesApi.attach(caseUid, saved.version, file)); setImpact(null);})),
		onRemoveAttachment: uid => detach(run(async () => {applyView(await paymentPlanUpdatesApi.removeAttachment(caseUid, view.version, uid)); setImpact(null);})),
		onSaveDraft: () => detach(run(async () => {await save();})),
		onReviewImpact: () => detach(run(async () => {const saved = await save(); setImpact(await paymentPlanUpdatesApi.preview(caseUid, saved.version));})),
		onDiscard: () => detach(run(() => discard(Boolean(!inlineStep)))),
		onSubmit: () => detach(run(() => submit(Boolean(!inlineStep)))),
	};
	if (inlineStep) return <InlineEditor editor={editor} />;
	const openEditor = (): void => {
		requestGeneration.current += 1;
		setLoading(false);
		openRef.current = true;
		setOpen(true);
		setNotice('');
		if (!stale) setError('');
		setImpact(null);
		const stepUid = view.stepUid;
		if (stepUid && view.editableFieldKeys !== undefined) detach(run(async () => setDialogStep(await getStepDetail(stepUid))));
	};
	return <Box sx={{my: 1.5}}>
		<EditorLauncher editor={editor} open={open} onOpen={openEditor} />
		<EditorDialog editor={editor} open={open} onClose={close} />
	</Box>;
}
